(ns ui.gallery.layout
  "The geometry of the virtualized gallery grid and the folder chain of
   the Replicant gallery: pure functions over plain numbers and maps, so
   they can be tested in Clojure and ClojureScript alike."
  (:require [clojure.string :as str]))

(def margin
  10)

(def width
  ;; of the preview images
  150)

(def height
  ;; of the preview images
  266.66)

(def row-height
  (+ height margin))

(def column-width
  (+ width margin))

(def min-page-size
  ;; at least this many entries are loaded at once:
  100)

(defn floor
  [x]
  #?(:clj (long (Math/floor (double x)))
     :cljs (js/Math.floor x)))

(defn ceil
  [x]
  #?(:clj (long (Math/ceil (double x)))
     :cljs (js/Math.ceil x)))

(defn calc-offsets
  "Calculates the offsets according to the scrolling position of the
   grid container:

   - `:per-row` how many entries are shown per row
   - `:rows-scrolled` how many rows are above the viewport
   - `:rows-visible` how many rows (also partially) fit into the viewport
   - `:offset` the index (zero-based) of the first visible entry
   - `:offset-end` the index of the last visible entry"
  [scroll-top client-width client-height]
  (let [per-row (max 1
                     (floor (/ client-width
                               column-width)))
        rows-scrolled (floor (/ scroll-top
                                row-height))
        rows-visible (ceil (/ client-height
                              row-height))]
    {:per-row per-row
     :rows-scrolled rows-scrolled
     :rows-visible rows-visible
     :offset (* rows-scrolled
                per-row)
     :offset-end (* (ceil (/ (+ scroll-top
                                client-height)
                             row-height))
                    per-row)}))

(defn calc-page-size
  "How many entries the gallery has to load at once: the rows that fit
   into the viewport, the extra row that the grid renders below it and
   two rows of overlap above and below, but at least `min-page-size`."
  [offsets]
  (max min-page-size
       (* (:per-row offsets)
          (+ (:rows-visible offsets)
             5))))

(defn visible-range
  "The indexes of the entries the grid renders: the visible rows plus
   one more row, otherwise there is so much empty space that the user
   might think the end of the gallery is reached."
  [offsets entry-count]
  (let [{:keys [per-row rows-visible offset]} offsets]
    (range offset
           (min (+ offset
                   (* per-row
                      (inc rows-visible)))
                entry-count))))

(defn total-rows
  [per-row entry-count]
  (ceil (/ entry-count
           (max 1 per-row))))

(defn grid-area
  "The value of the CSS property `grid-area` that places the entry with
   the index `n` into the grid with `per-row` columns."
  [per-row n]
  (let [row (floor (/ n
                      per-row))
        column (- n
                  (* per-row
                     row))]
    (str (inc row) "/" (inc column) "/" (inc row) "/" (inc column))))

(defn next-window
  "Decides whether the grid has to load another page: given the `loaded`
   window (`:offset` and `:page-size` of the loaded page) and the
   current scroll `offsets`, returns the new `:offset` and `:page-size`
   to load, or `nil` when the visible entries are inside the loaded
   window. Two rows of overlap are kept above and below the viewport."
  [loaded offsets]
  (let [loaded-start (or (:offset loaded) 0)
        loaded-end (+ loaded-start
                      (or (:page-size loaded)
                          min-page-size))
        page-size (calc-page-size offsets)
        visible-start (:offset offsets)
        visible-end (:offset-end offsets)
        scrolls-up? (< visible-start
                       loaded-start)
        scrolls-down? (> visible-end
                         loaded-end)
        overlap (* (:per-row offsets)
                   2)]
    (when (or scrolls-up?
              scrolls-down?)
      {:offset (if scrolls-up?
                 (max (- (+ visible-end
                            overlap)
                         page-size)
                      0)
                 (max (- visible-start
                         overlap)
                      0))
       :page-size page-size})))

(def max-visible-chain-length
  "How many folders the folder chain shows before its middle part
   collapses into an ellipsis, so that the root and the current folder
   stay visible for deeply nested folders."
  3)

(defn collapse-chain
  "Collapses the middle of a long `folder-chain` (a seq of folder uuids)
   into an `{:ellipsis [...]}` entry that carries the hidden uuids: the
   root folder and the last two folders stay visible."
  [folder-chain]
  (let [chain (vec folder-chain)]
    (if (<= (count chain)
            max-visible-chain-length)
      chain
      (into [(first chain)
             {:ellipsis (subvec chain
                                1
                                (- (count chain) 2))}]
            (take-last 2 chain)))))

(defn display-chain
  "The folder chain to show: the `:folder-chain` of the folder tree, or
   only the `:current-folder` while the organization has no folders yet.
   The gallery query returns no folder tree then (the top-level folder
   is created together with the first folder), but the tree is still
   shown, so the users learn that there are folders."
  [{:keys [folder-chain current-folder]}]
  (or (seq folder-chain)
      [current-folder]))

(defn sorted-subfolders
  "The `[uuid folder]` entries of the subfolders of `current-folder`,
   sorted by name regardless of case."
  [folders current-folder]
  (sort-by (fn [[_uuid {:keys [name]}]]
             (str/lower-case (str name)))
           (keep (partial find folders)
                 (get-in folders [current-folder :children]))))

(defn current-folder-name
  [folders current-folder]
  (or (get-in folders [current-folder :name])
      "Gallery"))
